import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineXMark,
  HiOutlineMusicalNote,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineDocumentText,
  HiOutlineSparkles,
  HiOutlineEye,
} from "react-icons/hi2";
import { AnimatedModal, modalPanelClass } from "./AnimatedModal";
import { ChordChartWithHover } from "./ChordChartWithHover";
import {
  CifraChordVersion,
  CifraImportPrefill,
  CifraSong,
  fetchCifraChords,
  fetchCifraImportData,
  fetchCifraYoutubeUrl,
} from "../utils/cifraApi";
import { getYouTubeEmbedUrl } from "../utils/youtubeLinks";

interface CifraPreviewModalProps {
  song: CifraSong;
  initialVersion?: CifraChordVersion;
  onClose: () => void;
  onImportReady: (prefill: CifraImportPrefill, notice?: string) => void;
}

const CifraPreviewModal: React.FC<CifraPreviewModalProps> = ({
  song,
  initialVersion = "principal",
  onClose,
  onImportReady,
}) => {
  const { t } = useTranslation();
  const hasSimplified = Boolean(song.imprimirUrlSimplificada);
  const [version, setVersion] = useState<CifraChordVersion>(
    initialVersion === "simplificada" && hasSimplified
      ? "simplificada"
      : "principal",
  );
  const [charts, setCharts] = useState("");
  const [notice, setNotice] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [cache, setCache] = useState<
    Partial<Record<CifraChordVersion, { charts: string; notice?: string }>>
  >({});

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotice("");

      const cached = cache[version];
      if (cached) {
        if (!cancelled) {
          setCharts(cached.charts);
          setNotice(cached.notice || "");
          setLoading(false);
        }
        return;
      }

      const result = await fetchCifraChords(song, version);
      if (cancelled) return;
      setCache((prev) => ({ ...prev, [version]: result }));
      setCharts(result.charts);
      setNotice(result.notice || "");
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
    // cache intentionally omitted — we read it synchronously for hits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song, version]);

  useEffect(() => {
    let cancelled = false;
    void fetchCifraYoutubeUrl(song).then((url) => {
      if (!cancelled) setYoutubeUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [song]);

  const handleImport = async () => {
    setImporting(true);
    try {
      // Reuse cached chords when possible to avoid a second fetch
      const cached = cache[version];
      if (cached?.charts) {
        onImportReady(
          {
            title: song.title,
            artist: song.artist,
            album: song.albumName,
            cifraclubUrl: song.cifraclubUrl,
            artistSlug: song.artistSlug,
            songSlug: song.songSlug,
            charts: cached.charts,
            ...(youtubeUrl ? { youtubeLink: youtubeUrl } : {}),
            artworkURL: song.artworkURL || undefined,
          },
          cached.notice,
        );
      } else {
        const { prefill, notice: importNotice } = await fetchCifraImportData(
          song,
          version,
        );
        onImportReady(prefill, importNotice);
      }
      onClose();
    } finally {
      setImporting(false);
    }
  };

  const embedUrl = youtubeUrl ? getYouTubeEmbedUrl(youtubeUrl) : null;

  return (
    <AnimatedModal
      isOpen
      onClose={onClose}
      contentClassName="sm:max-w-3xl lg:max-w-4xl"
    >
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>

        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 shrink-0 flex items-center justify-center">
              {song.artworkURL ? (
                <img
                  src={song.artworkURL}
                  alt={song.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <HiOutlineMusicalNote className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-xs font-medium text-[#5932EA] dark:text-[#c4b5fd] mb-0.5">
                <HiOutlineEye className="w-3.5 h-3.5" />
                {t("previewChords") || "Preview"}
              </div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 leading-snug line-clamp-2">
                {song.title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {song.artist}
                {song.albumName ? ` · ${song.albumName}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={song.cifraclubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 rounded-xl border border-[#5932EA]/20 dark:border-[#5932EA]/30 bg-[#5932EA]/5 dark:bg-[#5932EA]/10 text-[#5932EA]/80 dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/12 dark:hover:bg-[#5932EA]/20 transition-colors"
              aria-label={t("viewOnCifraClub") || "View on Cifra Club"}
              title={t("viewOnCifraClub") || "View on Cifra Club"}
            >
              <HiOutlineArrowTopRightOnSquare className="w-5 h-5" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={t("close")}
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-5 py-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div className="inline-flex p-1 rounded-xl bg-gray-100 dark:bg-gray-900/80 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setVersion("principal")}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                version === "principal"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              <HiOutlineDocumentText className="w-4 h-4" />
              {t("originalVersion") || "Original"}
            </button>
            <button
              type="button"
              onClick={() => hasSimplified && setVersion("simplificada")}
              disabled={!hasSimplified}
              title={
                hasSimplified
                  ? undefined
                  : t("simplifiedUnavailable") ||
                    "No simplified version for this song"
              }
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                version === "simplificada"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                  : hasSimplified
                    ? "text-gray-500 dark:text-gray-400"
                    : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
              }`}
            >
              <HiOutlineSparkles className="w-4 h-4" />
              {t("simplifiedVersion") || "Simplified"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-5 py-4 space-y-4">
          {notice && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 text-sm border border-amber-200 dark:border-amber-800">
              {notice}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
              <span className="w-10 h-10 border-2 border-[#5932EA]/25 border-t-[#5932EA] rounded-full animate-spin mb-3" />
              <p className="text-sm">{t("loadingChords") || "Loading chords…"}</p>
            </div>
          ) : charts.trim() ? (
            <div className="rounded-xl border border-[#5932EA]/15 dark:border-[#5932EA]/25 bg-[#5932EA]/5 dark:bg-[#5932EA]/10 backdrop-blur-sm p-4 sm:p-5">
              <ChordChartWithHover
                text={charts}
                className="text-gray-800 dark:text-gray-100 text-sm leading-relaxed"
                chordClassName="text-[#5932EA] dark:text-[#c4b5fd] font-semibold"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500 dark:text-gray-400">
              <HiOutlineDocumentText className="w-12 h-12 mb-3 opacity-40" />
              <p className="text-sm font-medium">
                {t("noChordsPreview") || "No chords to preview"}
              </p>
              <p className="text-xs mt-1 max-w-xs">
                {t("noChordsPreviewHint") ||
                  "You can still import and paste chords manually."}
              </p>
            </div>
          )}

          {embedUrl && (
            <div className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 aspect-video bg-black">
              <iframe
                src={embedUrl}
                title={`${song.title} YouTube`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>

        <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="sm:flex-1 h-11 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700/70 transition-colors"
          >
            {t("back") || t("close")}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing}
            className={`sm:flex-[2] h-11 rounded-xl text-sm font-medium backdrop-blur-sm disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors ${
              version === "simplificada"
                ? "border border-amber-300/50 dark:border-amber-500/35 bg-amber-50/80 dark:bg-amber-900/25 text-amber-800 dark:text-amber-200 hover:bg-amber-100/90 dark:hover:bg-amber-900/40"
                : "border border-[#5932EA]/40 dark:border-[#5932EA]/45 bg-[#5932EA]/12 dark:bg-[#5932EA]/20 text-[#4c2bd4] dark:text-[#c4b5fd] hover:bg-[#5932EA]/20 dark:hover:bg-[#5932EA]/30"
            }`}
          >
            {importing ? (
              <>
                <span className="w-4 h-4 border-2 border-[#5932EA]/25 border-t-[#5932EA] dark:border-[#c4b5fd]/30 dark:border-t-[#c4b5fd] rounded-full animate-spin" />
                {t("importing") || "Importing…"}
              </>
            ) : (
              <>
                {version === "simplificada" ? (
                  <HiOutlineSparkles className="w-4 h-4" />
                ) : (
                  <HiOutlineDocumentText className="w-4 h-4" />
                )}
                {version === "simplificada"
                  ? t("importSimplified") || "Import simplified"
                  : t("importOriginal") || "Import original"}
              </>
            )}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default CifraPreviewModal;
